import { useState, useEffect, useRef, type ReactNode } from "react";
import { VENDOR_FAQ_ITEMS } from "@/data/vendor-faq-items";
import { ShareFaqButton } from "@/components/share-faq-button";
import { usePageTitle } from "@/hooks/use-page-title";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Phone, Shield, CheckCircle, DollarSign, ClipboardList, Star, ChevronRight, ChevronLeft, Users, Building2, FileText, RefreshCw, MessageSquare, X, Bell, Sparkles, BookOpen, Menu, HelpCircle, User, Store, Layers } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { TourProvider, useTour } from "@/components/tour/TourProvider";
import { GuidebookDialog } from "@/components/tour/GuidebookDialog";
import { VendorGuideNudge } from "./vendor-portal/VendorGuideNudge";
import { TourAutoStarter } from "./vendor-portal/TourAutoStarter";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { ErrorBannerWithId } from "@/components/error-id-chip";
import { VendorImpersonationBanner } from "@/components/vendor-impersonation-banner";
import { useToast } from "@/hooks/use-toast";
import { useLocation, useParams, useSearch } from "wouter";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import { ACTIVE_TASK_STATUSES, EnrichedTask, HostCRM, VendorPortalLinkApplier, VendorProfile, VendorSession, formatCents, padpulseLogo } from "./vendor-portal/_shared";
import { NotificationBell, NotificationsTab } from "./vendor-portal/NotificationsTab";
import { TeamTab } from "./vendor-portal/TeamTab";
import { HostsTab } from "./vendor-portal/HostsTab";
import { PaymentsTab } from "./vendor-portal/PaymentsTab";
import { ArchiveSampleTaskDialog, TaskCard, TaskDetailView } from "./vendor-portal/tasks";
import { MarketplaceTab } from "./vendor-portal/MarketplaceTab";
import { ProfileTab } from "./vendor-portal/ProfileTab";

// ── Types ─────────────────────────────────────────────────────────────────────

type PortalState = "session_loading" | "phone_entry" | "vendor_picker" | "otp_verify" | "totp_verify" | "welcome_step" | "dashboard";

interface VendorPickerOption {
  vendorId: number;
  vendorName: string;
  businessName: string | null;
  teamMemberId: number;
}

// ── Root (auth flow) ──────────────────────────────────────────────────────────

const VENDOR_STATE_TITLES: Record<string, string> = {
  session_loading: "Vendor Portal",
  phone_entry: "Vendor Login",
  vendor_picker: "Vendor Login",
  otp_verify: "Vendor Verification",
  totp_verify: "Two-Factor Auth",
  welcome_step: "Welcome to PadPulse",
  dashboard: "Vendor Dashboard",
};

export default function VendorPortal() {
  // Start in "session_loading" so a refresh of /vendor-portal can restore an
  // existing vendor session (cookie still valid) without flashing the
  // phone-entry screen. The mount-time effect below calls
  // GET /api/vendor-portal/profile and either hydrates the session and
  // jumps to "dashboard" or falls back to "phone_entry" on 401/error.
  const [state, setState] = useState<PortalState>("session_loading");
  usePageTitle(VENDOR_STATE_TITLES[state] ?? "Vendor Portal");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Welcome step state
  const [welcomeTosAccepted, setWelcomeTosAccepted] = useState(false);
  const [welcomeMarketplaceOptIn, setWelcomeMarketplaceOptIn] = useState(true);
  const [welcomePending, setWelcomePending] = useState(false);
  const [welcomeError, setWelcomeError] = useState("");
  const [pickerOptions, setPickerOptions] = useState<VendorPickerOption[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState<number | null>(null);
  const [selectedTeamMemberId, setSelectedTeamMemberId] = useState<number | null>(null);
  const [session, setSession] = useState<VendorSession | null>(null);
  // Set when the mount-time session probe returns 401 — i.e. the vendor
  // *had* a cookie that the server rejected (expired / signed out
  // elsewhere / vendor was removed). Used to show a one-line notice on
  // the phone-entry screen so the vendor knows why they're being asked to
  // sign in again instead of landing on the dashboard.
  const [sessionExpiredNotice, setSessionExpiredNotice] = useState(false);
  // Pre-auth landing hint (set from the URL vendorId via a public lookup) —
  // true when this vendor is marketplace-blocked, so the landing-page upsell is
  // hidden before the vendor even signs in. Task #4449.
  const [landingMarketplaceBlocked, setLandingMarketplaceBlocked] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  // Read the optional :vendorId segment from the URL (present when the route
  // is /vendor-portal/:vendorId, absent on the legacy /vendor-portal entry).
  const params = useParams<{ vendorId?: string }>();
  const urlVendorId = params.vendorId ? parseInt(params.vendorId, 10) : null;

  // Navigate to the vendor-scoped URL for a given vendorId, replacing the
  // current history entry so back-navigation doesn't loop through the
  // pre-auth or generic /vendor-portal entry.
  const navigateToVendorPortal = (vendorId: number, search?: string) => {
    const path = `/vendor-portal/${vendorId}${search ? `?${search}` : ""}`;
    navigate(path, { replace: true });
  };

  // On mount, ask the server whether the existing session cookie still
  // resolves to a vendor. If yes, hydrate `session` and land on the
  // dashboard. If the cookie is missing/expired the server returns 401 and
  // we drop back to the phone-entry screen (with a notice on real 401s so
  // the vendor knows their session ended rather than just landing on a
  // bare login form).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/vendor-portal/profile", { credentials: "include" });
        if (cancelled) return;
        if (!res.ok) {
          if (res.status === 401) setSessionExpiredNotice(true);
          setState("phone_entry");
          return;
        }
        const data = await res.json();
        const hydratedSession: VendorSession = {
          vendorId: data.id,
          name: data.name,
          businessName: data.businessName ?? null,
          vendorTeamMemberId: data.vendorTeamMemberId ?? null,
          isOwner: data.isOwner,
          isAdmin: data.isAdmin,
          memberRole: data.memberRole ?? "owner",
          teamMemberName: data.teamMemberName ?? null,
          needsWelcome: data.needsWelcome,
          isMarketplaceBlocked: data.isMarketplaceBlocked ?? false,
        };
        setSession(hydratedSession);
        if (data.needsWelcome) {
          // Owner has never completed the welcome/ToS step — gate access.
          setState("welcome_step");
        } else {
          setState("dashboard");
          // Redirect to the vendor-scoped URL. If the URL already has the
          // correct vendor ID, replace is effectively a no-op in the browser.
          // If the URL has a different ID (support/QA visiting someone else's
          // link), redirect to the correct one.
          if (urlVendorId !== data.id) {
            navigateToVendorPortal(data.id, window.location.search.slice(1));
          }
        }
      } catch {
        if (!cancelled) setState("phone_entry");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Pre-auth landing hint: the SMS invite link carries the vendorId, so before
  // sign-in we look up (without a session) whether this vendor is
  // marketplace-blocked and, if so, suppress the marketplace upsell on the
  // landing page. Fail-safe: any error leaves the upsell visible. Task #4449.
  useEffect(() => {
    if (urlVendorId === null) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/vendor-portal/public-info/${urlVendorId}`, { credentials: "include" });
        if (cancelled || !res.ok) return;
        const data = await res.json();
        if (!cancelled) setLandingMarketplaceBlocked(!!data?.isMarketplaceBlocked);
      } catch {
        /* fail-safe: leave the upsell visible */
      }
    })();
    return () => { cancelled = true; };
  }, [urlVendorId]);

  const handleSendOTP = async () => {
    if (!phone || phone.replace(/\D/g, "").length < 10) {
      setError("Please enter a valid phone number");
      return;
    }
    setLoading(true);
    setError("");
    setSessionExpiredNotice(false);
    try {
      const res = await apiRequest("POST", "/api/vendor-portal/send-otp", { phone });
      const data = await res.json();
      if (data.picker) {
        setPickerOptions(data.options);
        setState("vendor_picker");
      } else {
        setState("otp_verify");
      }
    } catch (e: unknown) {
      setError(parseErrorMessage(e, "Failed to send verification code"));
    } finally {
      setLoading(false);
    }
  };

  const handlePickerSelect = async (vendorId: number, teamMemberId: number) => {
    setSelectedVendorId(vendorId);
    setSelectedTeamMemberId(teamMemberId);
    setLoading(true);
    setError("");
    try {
      await apiRequest("POST", "/api/vendor-portal/send-otp-for-vendor", { phone, vendorId, teamMemberId });
      setState("otp_verify");
    } catch (e: unknown) {
      setError(parseErrorMessage(e, "Failed to send verification code"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setError("Please enter the 6-digit code");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await apiRequest("POST", "/api/vendor-portal/verify-otp", { phone, code: otp });
      const data = await res.json();
      if (data?.needsTotp) {
        setTotpCode("");
        setState("totp_verify");
        return;
      }
      const newSession = data as VendorSession;
      setSession(newSession);
      if (newSession.needsWelcome) {
        setState("welcome_step");
      } else {
        setState("dashboard");
        navigateToVendorPortal(newSession.vendorId, window.location.search.slice(1));
      }
    } catch (e: unknown) {
      setError(parseErrorMessage(e, "Invalid verification code"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTotp = async () => {
    if (!totpCode || totpCode.length < 6) {
      setError("Please enter the 6-digit code from your authenticator app");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await apiRequest("POST", "/api/vendor-portal/auth/totp/verify", { totpCode });
      const data: VendorSession = await res.json();
      setSession(data);
      if (data.needsWelcome) {
        setState("welcome_step");
      } else {
        setState("dashboard");
        navigateToVendorPortal(data.vendorId, window.location.search.slice(1));
      }
    } catch (e: unknown) {
      setError(parseErrorMessage(e, "Invalid code. Try a backup code if you've lost your authenticator."));
    } finally {
      setLoading(false);
    }
  };

  const handleWelcomeStep = async () => {
    if (!welcomeTosAccepted) {
      setWelcomeError("You must accept the Terms of Service to continue.");
      return;
    }
    setWelcomePending(true);
    setWelcomeError("");
    try {
      await apiRequest("POST", "/api/vendor-portal/welcome-step", {
        tosAccepted: true,
        // Marketplace-blocked vendors can never opt in, regardless of the
        // (hidden) checkbox's default state. Task #4449.
        marketplaceOptIn: session?.isMarketplaceBlocked ? false : welcomeMarketplaceOptIn,
      });
      if (session) {
        const vendorId = session.vendorId;
        setState("dashboard");
        navigateToVendorPortal(vendorId, window.location.search.slice(1));
      }
    } catch (e: unknown) {
      setWelcomeError(parseErrorMessage(e, "Failed to save preferences. Please try again."));
    } finally {
      setWelcomePending(false);
    }
  };

  const handleLogout = async () => {
    try { await apiRequest("POST", "/api/vendor-portal/logout", {}); } catch {}
    queryClient.clear();
    setSession(null);
    setPhone("");
    setOtp("");
    setPickerOptions([]);
    setSelectedVendorId(null);
    setSelectedTeamMemberId(null);
    setState("phone_entry");
    navigate("/vendor-portal", { replace: true });
  };

  // Set document title for vendor portal sign-in screens
  useEffect(() => {
    if (state === "dashboard") return;
    const prev = document.title;
    document.title = "PadPulse Vendor Portal";
    return () => { document.title = prev; };
  }, [state]);

  if (state === "dashboard" && session) {
    return <VendorDashboard session={session} onLogout={handleLogout} />;
  }

  // Brief, deliberately quiet placeholder while the mount-time
  // /api/vendor-portal/profile probe is in flight. Avoids flashing the
  // phone-entry screen on a refresh of an already-authenticated portal.
  if (state === "session_loading") {
    return (
      <div
        className="min-h-screen bg-gradient-to-b from-brand-muted to-white flex flex-col items-center justify-center p-4 gap-4"
        data-testid="page-vendor-portal-loading"
      >
        <img src={padpulseLogo} alt="PadPulse" className="h-12 w-auto" />
        <RefreshCw className="h-5 w-5 text-brand animate-spin" aria-hidden="true" />
        <span className="sr-only">Restoring your vendor session…</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-brand-muted to-white p-4 md:p-8" data-testid="page-vendor-portal">
      <div className="max-w-5xl mx-auto flex flex-col md:flex-row md:items-start md:gap-10 lg:gap-16">

        {/* ── Login card (left on desktop, top on mobile) ── */}
        <div className="w-full md:w-[380px] shrink-0">
          <div className="text-center mb-8">
            <img
              src={padpulseLogo}
              alt="PadPulse"
              className="h-14 w-auto mx-auto mb-5"
            />
            <h1 className="text-2xl font-bold text-gray-900" data-testid="text-portal-title">PadPulse Vendor Portal</h1>
            <p className="text-sm text-gray-500 mt-2">Sign in to manage your tasks and team</p>
          </div>

          <Card>
            <CardContent className="p-6">
              {state === "phone_entry" && (
                <div className="space-y-4">
                  {sessionExpiredNotice && (
                    <div
                      className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-sm text-amber-800 dark:text-amber-200"
                      data-testid="text-session-expired-notice"
                    >
                      Your session has ended. Please sign in again to continue.
                    </div>
                  )}
                  <div>
                    <Label>Phone Number</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        className="pl-10"
                        placeholder="(555) 123-4567"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        type="tel"
                        data-testid="input-phone"
                        onKeyDown={(e) => e.key === "Enter" && handleSendOTP()}
                      />
                    </div>
                  </div>
                  {error && <ErrorBannerWithId message={error} testId="text-error" />}
                  <Button className="w-full bg-brand hover:bg-[#0043A6] text-white" onClick={handleSendOTP} disabled={loading} data-testid="button-send-otp">
                    {loading ? "Sending..." : "Send Verification Code"}
                  </Button>
                  <p className="text-xs text-gray-400 text-center">
                    We'll send a 6-digit code to verify your identity
                  </p>
                </div>
              )}

              {state === "vendor_picker" && (
                <div className="space-y-4">
                  <div className="text-center mb-2">
                    <p className="text-sm text-gray-600 font-medium">Multiple accounts found</p>
                    <p className="text-xs text-gray-400 mt-1">Select the vendor account you want to access</p>
                  </div>
                  {error && <ErrorBannerWithId message={error} testId="text-error" />}
                  {pickerOptions.map(opt => (
                    <Button
                      key={opt.vendorId}
                      variant="outline"
                      className="w-full justify-start h-auto py-3"
                      disabled={loading}
                      onClick={() => handlePickerSelect(opt.vendorId, opt.teamMemberId)}
                      data-testid={`button-pick-vendor-${opt.vendorId}`}
                    >
                      <div className="text-left">
                        <p className="font-medium">{opt.vendorName}</p>
                        {opt.businessName && <p className="text-xs text-gray-400">{opt.businessName}</p>}
                      </div>
                    </Button>
                  ))}
                  <button className="w-full text-sm text-blue-600 hover:underline" onClick={() => setState("phone_entry")} data-testid="button-back">
                    Use a different number
                  </button>
                </div>
              )}

              {state === "otp_verify" && (
                <div className="space-y-4">
                  <div className="text-center mb-2">
                    <p className="text-sm text-gray-600">Enter the code sent to</p>
                    <p className="font-medium">{phone}</p>
                  </div>
                  <div>
                    <Label>Verification Code</Label>
                    <Input
                      className="text-center text-2xl tracking-widest"
                      placeholder="000000"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      maxLength={6}
                      data-testid="input-otp"
                      onKeyDown={(e) => e.key === "Enter" && handleVerifyOTP()}
                    />
                  </div>
                  {error && <ErrorBannerWithId message={error} testId="text-error" />}
                  <Button className="w-full bg-brand hover:bg-[#0043A6] text-white" onClick={handleVerifyOTP} disabled={loading} data-testid="button-verify-otp">
                    {loading ? "Verifying..." : "Verify & Sign In"}
                  </Button>
                  <button className="w-full text-sm text-blue-600 hover:underline" onClick={() => setState("phone_entry")} data-testid="button-back">
                    Use a different number
                  </button>
                </div>
              )}

              {state === "totp_verify" && (
                <div className="space-y-4">
                  <div className="text-center mb-2">
                    <p className="text-sm text-gray-600 font-medium">Two-factor authentication</p>
                    <p className="text-xs text-gray-400 mt-1">Enter the 6-digit code from your authenticator app, or a backup code.</p>
                  </div>
                  <div>
                    <Label>Authenticator code</Label>
                    <Input
                      className="text-center text-2xl tracking-widest font-mono"
                      placeholder="000000"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value)}
                      data-testid="input-vendor-totp"
                      onKeyDown={(e) => e.key === "Enter" && handleVerifyTotp()}
                      autoFocus
                    />
                  </div>
                  {error && <ErrorBannerWithId message={error} testId="text-error" />}
                  <Button className="w-full bg-brand hover:bg-[#0043A6] text-white" onClick={handleVerifyTotp} disabled={loading} data-testid="button-vendor-totp-verify">
                    {loading ? "Verifying..." : "Verify & Sign In"}
                  </Button>
                  <button
                    className="w-full text-sm text-blue-600 hover:underline"
                    onClick={() => { setState("phone_entry"); setOtp(""); setTotpCode(""); setError(""); }}
                    data-testid="button-vendor-totp-back"
                  >
                    ← Cancel
                  </button>
                </div>
              )}

              {state === "welcome_step" && (
                <div className="space-y-5" data-testid="section-welcome-step">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-brand flex items-center justify-center mx-auto mb-3">
                      <Building2 className="h-6 w-6 text-white" />
                    </div>
                    <h2 className="text-lg font-bold text-gray-900">Welcome to PadPulse</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      A quick setup before you dive in.
                    </p>
                  </div>

                  {/* ToS */}
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Terms of Service</p>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      By using the PadPulse Vendor Portal you agree to our{" "}
                      <a href="https://www.padpulse.io/terms" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a href="https://www.padpulse.io/privacy" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">
                        Privacy Policy
                      </a>. You'll receive platform notifications for job assignments and messages.
                    </p>
                    <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="label-tos-accept">
                      <input
                        type="checkbox"
                        checked={welcomeTosAccepted}
                        onChange={e => setWelcomeTosAccepted(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand"
                        data-testid="checkbox-tos-accept"
                      />
                      <span className="text-sm text-gray-700">I accept the Terms of Service</span>
                    </label>
                  </div>

                  {/* Marketplace opt-in — hidden for marketplace-blocked vendors (Task #4449) */}
                  {!session?.isMarketplaceBlocked && (
                  <div className="rounded-md border border-blue-200 bg-blue-50 p-3 space-y-2">
                    <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Vendor Marketplace — First 3 months free</p>
                    <p className="text-xs text-blue-800 leading-relaxed">
                      The PadPulse Vendor Marketplace lets hosts in your area discover and hire you — no upfront cost. <strong>Your first 3 months are completely free</strong> with no fee at all. After that, you're only charged <strong>$19 in any calendar month</strong> where you accept a job from a new host (not the one who originally added you). No marketplace jobs that month = $0 fee.
                    </p>
                    <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="label-marketplace-optin">
                      <input
                        type="checkbox"
                        checked={welcomeMarketplaceOptIn}
                        onChange={e => setWelcomeMarketplaceOptIn(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-brand focus:ring-brand"
                        data-testid="checkbox-marketplace-optin"
                      />
                      <span className="text-sm text-blue-900 font-medium">List me in the Vendor Marketplace</span>
                    </label>
                  </div>
                  )}

                  {welcomeError && <ErrorBannerWithId message={welcomeError} testId="text-welcome-error" />}

                  <Button
                    className="w-full bg-brand hover:bg-[#0043A6] text-white"
                    onClick={handleWelcomeStep}
                    disabled={welcomePending || !welcomeTosAccepted}
                    data-testid="button-welcome-continue"
                  >
                    {welcomePending ? "Saving..." : "Continue to Dashboard →"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <p className="text-xs text-gray-400 text-center mt-6">
            Secure vendor management · Powered by PadPulse
          </p>
        </div>

        {/* ── Informational block (right on desktop, bottom on mobile) ── */}
        <div className="mt-8 md:mt-0 md:flex-1" data-testid="section-vendor-info">
          <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
          <div className="space-y-6">
            {/* Intro */}
            <div data-testid="block-what-is-padpulse">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-md bg-brand flex items-center justify-center shrink-0">
                  <Shield className="h-3.5 w-3.5 text-white" />
                </div>
                <h2 className="text-base font-bold text-gray-900">What is PadPulse?</h2>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                PadPulse is a property management platform that helps rental hosts coordinate maintenance, turnover cleaning, inspections, and other property work — all in one place. A host on PadPulse has invited you to their vendor network.
              </p>
            </div>

            <div className="h-px bg-gray-100" />

            {/* What is the Vendor Portal */}
            <div data-testid="block-what-is-portal">
              <h2 className="text-base font-bold text-gray-900 mb-2">What is the Vendor Portal?</h2>
              <p className="text-sm text-gray-500 mb-3">Your free workspace — no cost, no app to install:</p>
              <ul className="space-y-2" data-testid="list-portal-benefits">
                {[
                  { icon: <ClipboardList className="h-4 w-4 text-brand shrink-0 mt-0.5" />, text: "See every job assigned to you — with property details and notes" },
                  { icon: <CheckCircle className="h-4 w-4 text-brand shrink-0 mt-0.5" />, text: "Accept jobs, update status, and mark work complete from your phone" },
                  { icon: <MessageSquare className="h-4 w-4 text-brand shrink-0 mt-0.5" />, text: "Message the host directly — no more back-and-forth texts" },
                  { icon: <DollarSign className="h-4 w-4 text-brand shrink-0 mt-0.5" />, text: "Faster payouts through the platform — no chasing invoices (3% platform fee per payment)" },
                  { icon: <Star className="h-4 w-4 text-brand shrink-0 mt-0.5" />, text: "A clean job history for every host client, all in one place" },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600" data-testid={`benefit-item-${i}`}>
                    {item.icon}
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="h-px bg-gray-100" />

            {/* How it works */}
            <div data-testid="block-how-it-works">
              <h2 className="text-base font-bold text-gray-900 mb-3">How it works</h2>
              <ol className="space-y-2.5" data-testid="list-how-it-works">
                {[
                  "Host creates a job and assigns it to you",
                  "You get a notification with the job details",
                  "Open the portal, review the job, and accept it",
                  "Complete the work and mark it done",
                  "Get paid through the platform quickly and clearly",
                ].map((step, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-600" data-testid={`how-step-${i + 1}`}>
                    <span className="flex-none w-5 h-5 rounded-full bg-brand-muted text-brand text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Marketplace callout — hidden for marketplace-blocked vendors,
                including pre-sign-in via the URL vendorId hint (Task #4449) */}
            {!(session?.isMarketplaceBlocked || landingMarketplaceBlocked) && (
            <div className="rounded-lg border border-brand/20 bg-brand-muted p-4" data-testid="block-marketplace-callout">
              <div className="flex items-start gap-2 mb-1.5">
                <Building2 className="h-4 w-4 text-brand shrink-0 mt-0.5" />
                <p className="text-sm font-semibold text-brand">Want more work? Join the Vendor Marketplace</p>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                The PadPulse Vendor Marketplace lets other hosts in your area discover and hire you — expanding your client base beyond just the host who invited you. <strong>Free to list</strong> — your first 3 months have zero fees, then only $19 in months you accept a marketplace job. Completely optional. Once you sign in, opt in from <strong>Profile → Marketplace listing</strong>.
              </p>
            </div>
            )}
          </div>
          </CardContent>
          </Card>
        </div>

      </div>
    </div>
  );
}

function buildVendorPortalLinkApplier(args: {
  setActiveTab: (t: string) => void;
  setSelectedTaskId: (id: number | null) => void;
}): VendorPortalLinkApplier {
  return (link: string): boolean => {
    if (!link.startsWith("/vendor-portal")) return false;
    const qIndex = link.indexOf("?");
    const search = qIndex >= 0 ? link.slice(qIndex + 1) : "";
    if (!search) {
      args.setSelectedTaskId(null);
      return true;
    }
    const params = new URLSearchParams(search);
    const tab = params.get("tab");
    // Task #1968 — maintenance & operations tasks now share one feed.
    // Accept both `?task=` and the legacy `?maintenance=` deep-link.
    const taskParam = params.get("task") ?? params.get("maintenance");
    const formParam = params.get("form");
    if (taskParam) {
      const id = parseInt(taskParam, 10);
      if (!Number.isNaN(id)) args.setSelectedTaskId(id);
      return true;
    }
    if (formParam) {
      // Legacy `?form=<id>` deep-links (pre forms-on-tasks convergence) land on
      // the Tasks feed — link-based vendor forms no longer have a portal surface.
      args.setActiveTab("tasks");
      return true;
    }
    if (tab === "payouts") { args.setActiveTab("payments"); return true; }
    if (tab === "notifications") { args.setActiveTab("notifications"); return true; }
    if (tab === "forms") { args.setActiveTab("tasks"); return true; }
    if (tab === "tasks" || tab === "hosts" || tab === "payments" || tab === "team" || tab === "profile") {
      args.setActiveTab(tab);
      return true;
    }
    return false;
  };
}

function welcomeBannerStorageKey(vendorId: number) {
  return `vendor-portal-welcome-dismissed-${vendorId}`;
}

function WelcomeBanner({
  vendorId,
  hostName,
  onGoToProfile,
}: {
  vendorId: number;
  hostName: string | null;
  onGoToProfile: () => void;
}) {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(welcomeBannerStorageKey(vendorId)) === "1";
    } catch {
      return false;
    }
  });

  if (dismissed) return null;

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(welcomeBannerStorageKey(vendorId), "1");
    } catch {
      // ignore storage errors (private mode, quota, etc.)
    }
    setDismissed(true);
  };

  const greetingName = hostName?.trim() ? hostName.trim() : "Your host";

  return (
    <Alert
      className="mb-4 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 text-blue-900 dark:text-blue-200 [&>svg]:text-blue-600 dark:[&>svg]:text-blue-400 pr-10"
      data-testid="banner-vendor-welcome"
    >
      <Sparkles className="h-4 w-4" />
      <AlertTitle data-testid="text-vendor-welcome-title">Welcome to the Vendor Portal!</AlertTitle>
      <AlertDescription className="text-blue-900/90 dark:text-blue-200/90">
        <p className="mt-1" data-testid="text-vendor-welcome-body">
          {greetingName} will assign your first job here. You'll get an SMS when a new task arrives.
        </p>
        <div className="mt-2">
          <button
            type="button"
            onClick={onGoToProfile}
            className="text-sm font-medium text-blue-700 dark:text-blue-300 underline hover:text-blue-900 dark:hover:text-blue-100"
            data-testid="button-vendor-welcome-profile"
          >
            Complete your profile →
          </button>
        </div>
      </AlertDescription>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss welcome message"
        className="absolute right-2 top-2 rounded-md p-1 text-blue-700/70 hover:bg-blue-100 hover:text-blue-900"
        data-testid="button-vendor-welcome-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </Alert>
  );
}

// ── Vendor sidebar helpers ────────────────────────────────────────────────────

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

const VENDOR_SIDEBAR_LS_KEY = "padpulse:vendor-portal:sidebar-open";

function readVendorSidebarOpen(): boolean {
  try {
    const v = localStorage.getItem(VENDOR_SIDEBAR_LS_KEY);
    return v !== "false";
  } catch {
    return true;
  }
}

type VendorNavItem = {
  value: string;
  label: string;
  icon: typeof ClipboardList;
  ownerOnly?: boolean;
  accent?: boolean;
};

// Forms-on-tasks — the separate "Forms" nav entry is retired; form-tasks live
// in the Tasks feed behind the Forms filter chip.
const VENDOR_NAV_ITEMS: VendorNavItem[] = [
  { value: "tasks",         label: "Tasks",       icon: ClipboardList },
  { value: "notifications", label: "Alerts",      icon: Bell },
  { value: "hosts",         label: "Hosts",       icon: Building2 },
  { value: "team",          label: "Team",        icon: Users, ownerOnly: true },
  { value: "payments",      label: "Pay",         icon: DollarSign },
  { value: "profile",       label: "Profile",     icon: User },
  { value: "marketplace",   label: "Marketplace", icon: Store, ownerOnly: true, accent: true },
  { value: "faq",           label: "FAQ",         icon: HelpCircle },
];

function VendorSidebar({
  activeTab,
  setActiveTab,
  isOwner,
  isMarketplaceBlocked,
  sidebarOpen,
  setSidebarOpen,
  isMobile,
}: {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  isOwner: boolean;
  isMarketplaceBlocked: boolean;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  isMobile: boolean;
}) {
  // Task #4449 — a marketplace-blocked vendor never sees the Marketplace tab,
  // regardless of ownership.
  const visibleItems = VENDOR_NAV_ITEMS.filter(item => {
    if (item.value === "marketplace" && isMarketplaceBlocked) return false;
    return !item.ownerOnly || isOwner;
  });

  return (
    <aside
      className={`${isMobile ? "fixed inset-y-0 left-0 z-40" : "relative"} min-h-screen border-r border-gray-200 flex flex-col bg-white transition-all duration-300 overflow-hidden ${
        sidebarOpen ? "w-[200px]" : isMobile ? "w-[200px] -translate-x-full invisible" : "w-[48px]"
      }`}
      data-testid="vendor-sidebar"
    >
      <div className={`border-b border-gray-200 flex items-center ${sidebarOpen ? "px-3 py-3 justify-between" : "p-2 justify-center"}`}>
        {sidebarOpen ? (
          <>
            <div className="flex items-center gap-2 min-w-0">
              <img src={padpulseLogo} alt="PadPulse" className="h-6 w-auto shrink-0" />
              <span className="font-semibold text-gray-900 text-xs leading-tight truncate">Vendor Portal</span>
            </div>
            <button
              onClick={() => { setSidebarOpen(false); try { localStorage.setItem(VENDOR_SIDEBAR_LS_KEY, "false"); } catch {} }}
              className="p-1 rounded hover:bg-gray-100 text-gray-500 shrink-0"
              data-testid="button-vendor-sidebar-collapse"
              aria-label="Collapse sidebar"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          </>
        ) : (
          <button
            onClick={() => { setSidebarOpen(true); try { localStorage.setItem(VENDOR_SIDEBAR_LS_KEY, "true"); } catch {} }}
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
            data-testid="button-vendor-sidebar-expand"
            aria-label="Expand sidebar"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>

      <nav className="flex-1 py-2 overflow-y-auto" data-testid="vendor-sidebar-nav">
        <TooltipProvider delayDuration={300}>
          {visibleItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.value;
            return (
              <Tooltip key={item.value}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => setActiveTab(item.value)}
                    className={`w-full flex items-center gap-3 py-2.5 text-sm cursor-pointer transition-colors ${
                      sidebarOpen ? "px-4" : "px-0 justify-center"
                    } ${isActive ? "font-medium bg-brand-muted text-brand" : item.accent ? "text-brand-accent font-medium hover:bg-brand-accent-muted" : "text-gray-600 hover:bg-gray-50"}`}
                    data-testid={`button-vendor-nav-${item.value}`}
                    data-tour={`dashboard-tab-${item.value}`}
                    aria-current={isActive ? "page" : undefined}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {sidebarOpen && <span className="flex-1 text-left">{item.label}</span>}
                    {sidebarOpen && isActive && (
                      <span className="w-1 h-1 rounded-full bg-brand-accent shrink-0" aria-hidden="true" />
                    )}
                  </button>
                </TooltipTrigger>
                {!sidebarOpen && !isMobile && (
                  <TooltipContent side="right">{item.label}</TooltipContent>
                )}
              </Tooltip>
            );
          })}
        </TooltipProvider>
      </nav>

    </aside>
  );
}

// ── Vendor FAQ ────────────────────────────────────────────────────────────────

function FaqTab() {
  return (
    <div className="space-y-4" data-testid="tab-content-faq">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-semibold text-gray-900 text-base">Frequently Asked Questions</h3>
          <p className="text-xs text-gray-500 mt-0.5">Everything you need to know about working with PadPulse as a vendor.</p>
        </div>
        <ShareFaqButton path="/faq/vendors" testId="button-share-faq-vendor-portal" className="shrink-0" />
      </div>
      <Accordion type="single" collapsible className="w-full space-y-1">
        {VENDOR_FAQ_ITEMS.map((item, i) => (
          <AccordionItem
            key={i}
            value={`faq-${i}`}
            className="border border-gray-200 rounded-lg overflow-hidden px-3"
            data-testid={`faq-item-${i}`}
          >
            <AccordionTrigger className="text-sm font-medium text-left py-3 hover:no-underline">
              {item.q}
            </AccordionTrigger>
            <AccordionContent className="text-sm text-gray-600 pb-3 leading-relaxed">
              {item.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

// ── VendorDashboard ───────────────────────────────────────────────────────────

function VendorDashboard({ session, onLogout }: { session: VendorSession; onLogout: () => void }) {
  const { vendorId, isOwner } = session;
  // Set document title for the authenticated dashboard
  useEffect(() => {
    const prev = document.title;
    document.title = "PadPulse Vendor Portal";
    return () => { document.title = prev; };
  }, []);
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [activeTab, setActiveTab] = useTabQueryParam("tab", "tasks", {
    tabs: ["tasks", "hosts", "payments", "notifications", "team", "marketplace", "profile", "faq"],
  });
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(() => isMobile ? false : readVendorSidebarOpen());

  // Task #2113 — `?task=<id>` / `?maintenance=<id>` / `?form=<id>` are now
  // the source of truth for the open task or form. Deriving them from the
  // URL (reactive via wouter's useSearch) means a refresh restores the
  // detail view that was open, and browser back/forward naturally moves
  // between the list and the detail. The corresponding setters write to
  // the URL via wouter `navigate` (push, not replace, so back works).
  const [pathname, navigate] = useLocation();
  const search = useSearch();
  const searchParams = new URLSearchParams(search);
  const taskParamRaw = searchParams.get("task") ?? searchParams.get("maintenance");
  const selectedTaskId = (() => {
    if (!taskParamRaw) return null;
    const n = parseInt(taskParamRaw, 10);
    return Number.isNaN(n) ? null : n;
  })();
  const setSelectedTaskId = (id: number | null) => {
    const next = new URLSearchParams(search);
    if (id === null) {
      next.delete("task");
      next.delete("maintenance");
    } else {
      next.set("task", String(id));
      next.delete("maintenance");
    }
    const qs = next.toString();
    navigate(qs ? `${pathname}?${qs}` : pathname);
  };
  // Task #1348 — lifted up to the dashboard so the dialog survives the
  // re-render that moves a just-completed sample task between Active and
  // Completed tabs (which would otherwise unmount the TaskCard owning local
  // dialog state).
  const [showSampleArchiveDialog, setShowSampleArchiveDialog] = useState(false);
  // Tracks whether the Hosts tab is showing the per-host detail sub-view, so
  // the Guide button can replay the matching tour for the screen the vendor
  // is actually looking at.
  const [hostsSubView, setHostsSubView] = useState<"list" | "hostDetail">("list");
  // Set true when the Guidebook starts the per-host detail tour from another
  // screen — the HostsTab consumes this to open its first host card so the
  // detail tour has targets to spotlight, then clears it.
  const [pendingHostDetailOpen, setPendingHostDetailOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Routes /vendor-portal?... deep-links into local dashboard state so the
  // bell + Alerts tab can re-target tabs/tasks/forms even when the vendor is
  // already inside the portal.
  const applyInternalLink = buildVendorPortalLinkApplier({
    setActiveTab,
    setSelectedTaskId,
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    const connectedParam = params.get("connected");
    if (tabParam === "payouts") {
      setActiveTab("payments");
      if (connectedParam === "1") {
        fetch("/api/vendor-portal/payout-account/refresh-status", { method: "POST", credentials: "include" })
          .then(r => r.ok ? r.json() : null)
          .then(updated => {
            if (updated) queryClient.setQueryData(["vendor", vendorId, "payout-account"], updated);
          })
          .catch(() => {});
      }
    }
  }, []);

  const { data: tasks = [], isLoading: tasksLoading } = useQuery<EnrichedTask[]>({
    queryKey: ["vendor", vendorId, "tasks", showAllTasks],
    queryFn: async () => {
      const url = showAllTasks ? "/api/vendor-portal/tasks?showAll=true" : "/api/vendor-portal/tasks";
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || `Request failed: ${r.status}`);
      return r.json();
    },
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 0,
  });

  const { data: vendor, isLoading: vendorLoading } = useQuery<VendorProfile>({
    queryKey: ["vendor", vendorId, "profile"],
    queryFn: async () => {
      const r = await fetch("/api/vendor-portal/profile", { credentials: "include" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || `Request failed: ${r.status}`);
      return r.json();
    },
  });

  const owningHostId = vendor?.userId ?? null;
  const { data: hostsForBanner = [] } = useQuery<HostCRM[]>({
    queryKey: ["vendor", vendorId, "hosts"],
    queryFn: async () => {
      const r = await fetch("/api/vendor-portal/hosts", { credentials: "include" });
      if (!r.ok) return [];
      return r.json();
    },
    enabled: owningHostId != null,
  });
  const owningHostName = owningHostId != null
    ? (hostsForBanner.find(h => h.hostId === owningHostId)?.hostName ?? null)
    : null;

  // Task #1718 — Active = anything not in a terminal state. Per
  // OPERATIONS_TASK_STATUSES in shared/schema.ts, terminal statuses are
  // completed/approved/paid/rejected/cancelled (also "settled" used by
  // older rows). Everything else (pending/assigned/accepted/in_progress/
  // needs_attention) counts as active. Task #1968 — maintenance tickets
  // live in the same unified `tasks` table now, so this single filter
  // covers both operations + maintenance jobs.
  const OPS_TERMINAL_STATUSES = ["completed", "settled", "paid", "approved", "rejected", "cancelled"];
  const activeTasks = tasks.filter(t => !OPS_TERMINAL_STATUSES.includes(t.status));
  const completedTasks = tasks.filter(t => OPS_TERMINAL_STATUSES.includes(t.status));
  // Forms-on-tasks — the Forms sub-tab shows every form-task regardless of status.
  const formTasks = tasks.filter(t => t.formTemplateId != null);
  const totalActiveCount = activeTasks.length;
  const totalCompletedCount = completedTasks.length;
  const hasNeverAcceptedTask = !tasksLoading
    && tasks.every(t => !ACTIVE_TASK_STATUSES.includes(t.status));
  const showWelcomeBanner = !tasksLoading && !vendorLoading && hasNeverAcceptedTask;

  // Task #4171 — render a task list with dispatched batch jobs grouped under a
  // single colored "Batch #N" container. Batched tasks float to the top
  // (contiguous by batchId); standalone tasks follow in order. Each batch
  // group is wrapped in a distinct indigo card so vendors immediately see
  // which jobs are part of the same work order.
  const renderGroupedTasks = (list: EnrichedTask[], withTour: boolean) => {
    const sorted = [...list].sort((a, b) => {
      const ba = a.batchId ?? Number.MAX_SAFE_INTEGER;
      const bb = b.batchId ?? Number.MAX_SAFE_INTEGER;
      return ba - bb;
    });

    // Pre-group contiguous tasks by batchId.
    const groups: Array<{ batchId: number | null; label?: string; tasks: EnrichedTask[] }> = [];
    for (const task of sorted) {
      const bId = task.batchId ?? null;
      const last = groups.at(-1);
      if (last && last.batchId === bId) {
        last.tasks.push(task);
      } else {
        const label = bId != null
          ? (task.batchTitle
              ? `${task.batchTitle} · Batch #${task.batchNumber}`
              : `Batch #${task.batchNumber}`)
          : undefined;
        groups.push({ batchId: bId, label, tasks: [task] });
      }
    }

    let isFirstOverall = true;
    const out: ReactNode[] = [];
    for (const { batchId, label, tasks: groupTasks } of groups) {
      if (batchId != null) {
        // Batched group — wrap all cards in a tinted indigo container so the
        // vendor can tell at a glance these jobs belong to one work order.
        const batchCards = groupTasks.map(task => {
          const cardIsFirst = withTour && isFirstOverall;
          isFirstOverall = false;
          return (
            <TaskCard
              key={`op-${task.id}`}
              task={task}
              vendorId={vendorId}
              session={session}
              onOpen={() => setSelectedTaskId(task.id)}
              queryClient={queryClient}
              toast={toast}
              isFirst={cardIsFirst}
              onSampleCompleted={() => setShowSampleArchiveDialog(true)}
            />
          );
        });
        out.push(
          <div
            key={`batch-grp-${batchId}`}
            className="rounded-xl border border-indigo-200 bg-indigo-50 p-3 space-y-2"
            data-testid={`vendor-batch-group-${batchId}`}
          >
            <div
              className="flex items-center gap-2 pb-1"
              data-testid={`vendor-batch-header-${batchId}`}
            >
              <Layers className="h-4 w-4 text-indigo-600 shrink-0" />
              <span className="text-sm font-semibold text-indigo-800">{label}</span>
              <span className="text-xs text-indigo-500">grouped work order</span>
            </div>
            {batchCards}
          </div>,
        );
      } else {
        // Standalone tasks — render cards directly, no container.
        for (const task of groupTasks) {
          const cardIsFirst = withTour && isFirstOverall;
          isFirstOverall = false;
          out.push(
            <TaskCard
              key={`op-${task.id}`}
              task={task}
              vendorId={vendorId}
              session={session}
              onOpen={() => setSelectedTaskId(task.id)}
              queryClient={queryClient}
              toast={toast}
              isFirst={cardIsFirst}
              onSampleCompleted={() => setShowSampleArchiveDialog(true)}
            />,
          );
        }
      }
    }
    return out;
  };

  if (selectedTaskId !== null) {
    const task = tasks.find(t => t.id === selectedTaskId);
    return (
      <>
        <TaskDetailView
          taskId={selectedTaskId}
          task={task}
          vendorId={vendorId}
          session={session}
          showAll={showAllTasks}
          onBack={() => {
            // Task #2113 — setSelectedTaskId now writes the URL via wouter
            // navigate (it's derived from `?task=` / `?maintenance=`), so
            // clearing it both pops the detail view and strips the param,
            // preserving the rest of the query string (e.g. `?tab=`).
            setSelectedTaskId(null);
          }}
          onLogout={onLogout}
          queryClient={queryClient}
          onSampleCompleted={() => setShowSampleArchiveDialog(true)}
        />
        <ArchiveSampleTaskDialog
          open={showSampleArchiveDialog}
          onOpenChange={setShowSampleArchiveDialog}
          vendorId={vendorId}
          queryClient={queryClient}
          toast={toast}
        />
      </>
    );
  }

  function handleNavChange(tab: string) {
    setActiveTab(tab);
    if (isMobile) setSidebarOpen(false);
  }

  return (
    <TourProvider>
      <TourAutoStarter activeTab={activeTab} />
      <div className="min-h-screen flex flex-col bg-gray-50" data-testid="page-vendor-dashboard">
        <VendorImpersonationBanner />

        {/* Body: sidebar + main content */}
        <div className="flex flex-1 min-h-0">

        {/* Mobile sidebar backdrop */}
        {isMobile && sidebarOpen && (
          <div className="fixed inset-0 bg-black/40 z-30" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Left sidebar */}
        <VendorSidebar
          activeTab={activeTab}
          setActiveTab={handleNavChange}
          isOwner={!!isOwner}
          isMarketplaceBlocked={!!session.isMarketplaceBlocked}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          isMobile={isMobile}
        />

        {/* Main content column */}
        <div className="flex-1 min-w-0 flex flex-col">
          <header className="bg-white border-b px-4 py-3 shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2" data-tour="dashboard-brand">
                {isMobile && (
                  <button
                    onClick={() => setSidebarOpen(true)}
                    className="p-1.5 rounded hover:bg-gray-100 text-gray-500 mr-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
                    data-testid="button-vendor-open-sidebar"
                    aria-label="Open navigation"
                  >
                    <Menu className="h-5 w-5" />
                  </button>
                )}
                <img src={padpulseLogo} alt="PadPulse" className="h-8 w-auto shrink-0" />
                <div className="hidden sm:block">
                  <span className="font-semibold text-gray-900 text-sm">
                    <span className="text-brand">PadPulse</span>{" "}
                    <span className="text-gray-500 font-normal">Vendor Portal</span>
                  </span>
                  {session.teamMemberName && (
                    <p className="text-xs text-gray-400">{session.teamMemberName} · {isOwner ? "Owner" : "Member"}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <NotificationBell
                  onViewAll={() => handleNavChange("notifications")}
                  applyInternalLink={applyInternalLink}
                />
                <GuideButton activeTab={activeTab} hostsSubView={hostsSubView} isOwner={isOwner ?? false} onNavigateTab={handleNavChange} vendorId={vendorId} onOpenHostDetail={() => setPendingHostDetailOpen(true)} />
                <Button variant="ghost" size="sm" onClick={onLogout} data-testid="button-logout">Sign Out</Button>
              </div>
            </div>
          </header>

          <div className="flex-1 overflow-auto">
            <div className="max-w-2xl mx-auto p-4">
              <div className="grid grid-cols-3 gap-3 mb-6" data-tour="dashboard-stats">
                <Card>
                  <CardContent className="p-3 text-center">
                    <ClipboardList className="h-5 w-5 text-blue-600 mx-auto mb-1" />
                    <p className="text-lg font-semibold" data-testid="text-active-tasks">{tasksLoading ? "—" : totalActiveCount}</p>
                    <p className="text-xs text-gray-500">Active</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <DollarSign className="h-5 w-5 text-green-600 mx-auto mb-1" />
                    <p className="text-lg font-semibold" data-testid="text-earned">
                      {tasksLoading ? "—" : formatCents(completedTasks.reduce((s, t) => s + (t.priceCents || 0), 0))}
                    </p>
                    <p className="text-xs text-gray-500">Earned</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <Star className="h-5 w-5 text-yellow-500 mx-auto mb-1" />
                    <p className="text-lg font-semibold" data-testid="text-rating">
                      {vendorLoading ? "—" : (vendor?.aggregateRating ? parseFloat(vendor.aggregateRating).toFixed(1) : "N/A")}
                    </p>
                    <p className="text-xs text-gray-500">Rating</p>
                  </CardContent>
                </Card>
              </div>

              <VendorGuideNudge activeTab={activeTab} hostsSubView={hostsSubView} />

              <Tabs value={activeTab} onValueChange={setActiveTab}>

          {/* ── Tasks tab ── */}
          <TabsContent value="tasks" className="mt-4">
            {showWelcomeBanner && (
              <WelcomeBanner
                vendorId={vendorId}
                hostName={owningHostName}
                onGoToProfile={() => setActiveTab("profile")}
              />
            )}
            {!isOwner && (
              <div className="flex items-center justify-between mb-3 px-1">
                <p className="text-xs text-gray-500">{showAllTasks ? "Showing all team tasks (read-only)" : "Showing your assigned tasks"}</p>
                <button
                  onClick={() => setShowAllTasks(v => !v)}
                  className="text-xs text-blue-600 underline"
                  data-testid="button-toggle-show-all-tasks"
                >
                  {showAllTasks ? "Show my tasks" : "See all team tasks"}
                </button>
              </div>
            )}
            <Tabs defaultValue="active">
              <TabsList className="w-full grid grid-cols-3 mb-4" data-tour="tasks-tab-switcher">
                <TabsTrigger value="active">Active ({totalActiveCount})</TabsTrigger>
                <TabsTrigger value="forms" data-testid="tab-form-tasks">Forms ({formTasks.length})</TabsTrigger>
                <TabsTrigger value="completed">Completed ({totalCompletedCount})</TabsTrigger>
              </TabsList>
              <TabsContent value="active" className="space-y-3" data-tour="tasks-active-list">
                {tasksLoading ? (
                  <p className="text-sm text-gray-400 text-center py-8">Loading tasks...</p>
                ) : activeTasks.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">No active tasks.</p>
                ) : (
                  // Task #1968 — operations + maintenance tasks now share the
                  // unified `tasks` feed, so a single TaskCard handles both.
                  // Task #4171 — dispatched batch jobs are grouped under one
                  // "Batch #N" header so the vendor reads them as one work order.
                  renderGroupedTasks(activeTasks, true)
                )}
              </TabsContent>
              {/* Forms-on-tasks — form-tasks only (any status), the converged
                  replacement for the retired standalone Forms tab. */}
              <TabsContent value="forms" className="space-y-3" data-testid="list-form-tasks">
                {tasksLoading ? (
                  <p className="text-sm text-gray-400 text-center py-8">Loading forms...</p>
                ) : formTasks.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">No forms yet. Forms sent by your hosts show up here.</p>
                ) : (
                  renderGroupedTasks(formTasks, false)
                )}
              </TabsContent>
              <TabsContent value="completed" className="space-y-3">
                {completedTasks.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">No completed tasks yet.</p>
                ) : (
                  renderGroupedTasks(completedTasks, false)
                )}
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* ── Hosts CRM tab ── */}
          <TabsContent value="hosts" className="mt-4">
            <HostsTab vendorId={vendorId} isOwner={isOwner || false} canManageAssignments={(session.isAdmin ?? isOwner) || false} owningHostId={vendor?.userId ?? null} onSubViewChange={setHostsSubView} openDetailRequest={pendingHostDetailOpen} onConsumeOpenDetail={() => setPendingHostDetailOpen(false)} />
          </TabsContent>

          {/* ── Payments tab ── */}
          <TabsContent value="payments" className="mt-4">
            <PaymentsTab vendorId={vendorId} isOwner={isOwner || false} />
          </TabsContent>

          {/* ── Notifications tab (Task #1258) ── */}
          <TabsContent value="notifications" className="mt-4">
            <NotificationsTab applyInternalLink={applyInternalLink} />
          </TabsContent>

          {/* ── Team tab (owner only) ── */}
          {isOwner && (
            <TabsContent value="team" className="mt-4">
              <TeamTab vendorId={vendorId} currentMemberId={session.vendorTeamMemberId || null} queryClient={queryClient} toast={toast} />
            </TabsContent>
          )}

          {/* ── Marketplace tab (owner-only; hidden entirely when the vendor is
               marketplace-blocked — Task #4449) ── */}
          {isOwner && !session.isMarketplaceBlocked && (
            <TabsContent value="marketplace" className="mt-4">
              <MarketplaceTab
                vendor={vendor}
                vendorLoading={vendorLoading}
                vendorId={vendorId}
                session={session}
                queryClient={queryClient}
                toast={toast}
              />
            </TabsContent>
          )}

          {/* ── Profile tab ── */}
          <TabsContent value="profile" className="mt-4">
            <ProfileTab
              vendor={vendor}
              vendorLoading={vendorLoading}
              vendorId={vendorId}
              session={session}
              queryClient={queryClient}
              toast={toast}
            />
          </TabsContent>

          {/* ── FAQ tab ── */}
          <TabsContent value="faq" className="mt-4">
            <FaqTab />
          </TabsContent>
        </Tabs>
            </div>{/* max-w-2xl */}
          </div>{/* flex-1 overflow-auto */}
        </div>{/* main content column */}
        </div>{/* body: sidebar + main content */}
        <ArchiveSampleTaskDialog
          open={showSampleArchiveDialog}
          onOpenChange={setShowSampleArchiveDialog}
          vendorId={vendorId}
          queryClient={queryClient}
          toast={toast}
        />
      </div>{/* min-h-screen flex */}
    </TourProvider>
  );
}

// ── Guided product tour helpers ──────────────────────────────────────────────

// Opens the Guidebook hub popup so vendors can pick (or replay) any of the
// available product tours from one place. The hub highlights the tour for
// the active tab (and the Hosts detail sub-view when applicable) as
// "For this page" so the per-tab affordance from the original Guide button
// is preserved.
function GuideButton({ activeTab, hostsSubView, isOwner, onNavigateTab, vendorId, onOpenHostDetail }: { activeTab: string; hostsSubView: "list" | "hostDetail"; isOwner: boolean; onNavigateTab: (tab: string) => void; vendorId: number; onOpenHostDetail: () => void }) {
  const [open, setOpen] = useState(false);
  // Only used to decide whether to surface the per-host detail tour. Shares the
  // HostsTab query key so it reuses the cache when available, and only fires
  // once the Guidebook is opened.
  const { data: hosts = [] } = useQuery<HostCRM[]>({
    queryKey: ["vendor", vendorId, "hosts"],
    queryFn: async () => {
      const r = await fetch("/api/vendor-portal/hosts", { credentials: "include" });
      if (!r.ok) return [];
      return r.json();
    },
    enabled: open,
  });
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        data-tour="dashboard-guide-button"
        data-testid="button-guide"
        className="gap-1.5"
        aria-label="Open the Guidebook"
        title="Open the Guidebook — every guided tour in one place"
      >
        <BookOpen className="h-4 w-4" />
        <span className="hidden sm:inline">Guidebook</span>
      </Button>
      <GuidebookDialog open={open} onOpenChange={setOpen} activeTab={activeTab} hostsSubView={hostsSubView} isOwner={isOwner} onNavigateTab={onNavigateTab} onOpenHostDetail={onOpenHostDetail} hasHosts={hosts.length > 0} />
    </>
  );
}
